import { useEffect } from 'react';
import { useAlerts } from '../lib/useManagerData';
/* THE KIT'S OWN FACE. `fonts.css` embeds General Sans — the family every
   artboard is typeset in — and nothing outside the dev kit gallery imports it,
   so every ported screen in this app is drawn in `--font-primary` ("Noto Sans
   Arabic", Tajawal, Cairo, system-ui) instead. The metrics differ by about two
   per cent, which is invisible on a word and decisive on a line: paragraphs
   wrap one word earlier or later than the board does. The import is global (a
   @font-face always is) but inert — only the scoped rule in manager.css §9
   asks for the family, so no other lane's screens change. The one-line fix
   belongs in main.tsx; it is written up under "Shared changes needed". */
import '@/components/kit/fonts.css';
import './manager.css';

/**
 * THE FOUR THINGS THIS SURFACE NEEDS FROM THE SHELL AND THE SHELL DOES NOT
 * GIVE IT — done from inside the lane, because the files that ought to do them
 * are on the brief's never-edit list while eight other agents are reading them.
 * The exact diffs are in reports/clinic-manager.md under "Shared changes
 * needed"; this is the work-around the brief asks for in the meantime, and it
 * is inert the moment those diffs land.
 *
 *  1. `data-cm-surface` on <body> — the hook every rule in manager.css hangs
 *     off, so none of them can reach another role's screens. Set on mount,
 *     removed on unmount.
 *  2. `data-cm-unread` — the bell's dot. Every board draws it; ShellChrome has
 *     no `hasUnread` field, so TopBar's defaults to false forever.
 *  3. THE AUDIT LOG DOOR. `designRoles.ts` gates it on `Permission.AUDIT_READ`
 *     (`audit:read`); the backend's Clinic Manager role is granted
 *     `audit_logs:read` and holds `audit:read` nowhere (permission-catalog.ts
 *     ~244). `hasPermission` is a strict `includes`, so MaieRail filters the
 *     door out of every real manager's rail — the board draws five doors and
 *     the app four, and /clinic-manager/audit lights nothing at all, although
 *     the route reads 1,826 real rows for that same account.
 *
 *     So the door is mounted into the rail's own <nav> through a portal, in
 *     the board's position (after Reports, before System Settings), from the
 *     same kit markup MaieRail's `DesignDoorRow` uses — and ONLY while the
 *     permission is missing, so it disappears rather than doubles the day the
 *     shared gate is corrected.
 *  4. `--cm-srch-max` — the top bar's search field, on the one screen whose
 *     artboard sizes it narrower than the canvas's own cap. See the effect.
 */
export function ManagerSurface({ searchWidth }: { searchWidth?: number } = {}) {
  /* THE DOT AND THE ALERTS SCREEN ARE ONE STATEMENT. `/notifications/unread-count`
     is scoped to the signed-in USER and answers 0 for this account, while the
     queue the Alerts door opens is the CLINIC's (`GET /notifications` is
     tenant-scoped and returns nine, seven of them unread) — a dark bell over a
     screen that says nine are waiting is two answers to one question. So this
     counts the same rows that screen counts, from the same cached query. */
  const alerts = useAlerts();

  /* THE AUDIT DOOR IS THE RAIL'S AGAIN. This surface used to inject its own
     "Audit log" row, because designRoles gated the shared one on `audit:read`
     — a permission no role is granted — so it never drew. That gate now asks
     for `audit_logs:read`, which the Clinic Manager holds, and the rail draws
     the door itself. Injecting a second one put "Audit log" in the rail twice
     and pushed System Settings 44px down every settings board. */

  useEffect(() => {
    document.body.dataset.cmSurface = '1';
    return () => { delete document.body.dataset.cmSurface; };
  }, []);

  /* 4. THE TOP BAR'S SEARCH FIELD, ON A SCREEN WHOSE ARTBOARD IS NARROWER THAN
        THE CAP. The canvas flex-sizes that field; the app, working around
        kit-extras.css's 520px cap, content-sizes it off its own placeholder
        (manager.css §2). The two agree within 2px everywhere except `g_set`,
        whose long title column leaves the artboard's row 361px where the
        placeholder measures 368. A screen that knows its board's number says
        so, and manager.css reads it as `--cm-srch-max`. */
  useEffect(() => {
    if (!searchWidth) return;
    document.body.style.setProperty('--cm-srch-max', `${searchWidth}px`);
    document.body.dataset.cmSrch = String(searchWidth);
    return () => {
      document.body.style.removeProperty('--cm-srch-max');
      delete document.body.dataset.cmSrch;
    };
  }, [searchWidth]);

  const unreadCount = alerts.page.rows.filter(
    (a) => !a.acknowledgedAt && (a.requiresAcknowledgment || !a.readAt)).length;
  useEffect(() => {
    if (unreadCount > 0) document.body.dataset.cmUnread = '1';
    else delete document.body.dataset.cmUnread;
    return () => { delete document.body.dataset.cmUnread; };
  }, [unreadCount]);

  return null;
}
